<script lang="ts">
	/**
	 * GlowCard - Interactive card with glow effect and 3D tilt on hover
	 *
	 * @example
	 * <GlowCard class="p-6">
	 *   <h3>Card Title</h3>
	 *   <p>Card content</p>
	 * </GlowCard>
	 */
	import type { Snippet } from 'svelte';
	import { calculateTilt, calculateGlowPosition, TILT_RESET } from '$lib/utils/tilt';
	import { prefersReducedMotion, isMobileDevice } from '$lib/utils/motion';

	interface Props {
		/** Additional CSS classes */
		class?: string;
		children: Snippet;
	}

	let { class: className = '', children }: Props = $props();

	let card: HTMLElement;
	let glowX = $state(50);
	let glowY = $state(50);
	let rotateX = $state(0);
	let rotateY = $state(0);
	let _isHovered = $state(false);

	function handleMouseMove(e: MouseEvent) {
		if (prefersReducedMotion() || isMobileDevice()) return;

		const glow = calculateGlowPosition(e, card);
		glowX = glow.x;
		glowY = glow.y;

		const tilt = calculateTilt(e, card);
		rotateX = tilt.rotateX;
		rotateY = tilt.rotateY;
	}

	function handleMouseLeave() {
		_isHovered = false;
		rotateX = TILT_RESET.rotateX;
		rotateY = TILT_RESET.rotateY;
	}
</script>

<div
	bind:this={card}
	class="relative group perspective-1000 {className}"
	onmouseenter={() => (_isHovered = true)}
	onmouseleave={handleMouseLeave}
	onmousemove={handleMouseMove}
	role="presentation"
>
	<!-- Outer glow effect -->
	<div
		class="hidden md:block absolute -inset-4 rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-700 ease-out pointer-events-none blur-2xl"
		style="
			background: radial-gradient(
				400px circle at {glowX}% {glowY}%,
				rgba(139, 92, 246, 0.08),
				transparent 50%
			);
		"
	></div>

	<!-- Glow effect -->
	<div
		class="hidden md:block absolute -inset-px rounded-xl opacity-0 group-hover:opacity-100 transition-all duration-500 ease-out pointer-events-none"
		style="
			background: radial-gradient(
				600px circle at {glowX}% {glowY}%,
				rgba(255, 255, 255, 0.06),
				transparent 40%
			);
		"
	></div>

	<!-- Border glow -->
	<div
		class="hidden md:block absolute -inset-px rounded-xl opacity-0 group-hover:opacity-100 transition-all duration-500 ease-out pointer-events-none"
		style="
			background: radial-gradient(
				400px circle at {glowX}% {glowY}%,
				rgba(255, 255, 255, 0.15),
				transparent 40%
			);
			mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
			mask-composite: exclude;
			padding: 1px;
		"
	></div>

	<!-- Content -->
	<div
		class="glow-card-inner relative bg-white/[0.02] md:backdrop-blur-sm rounded-xl border border-white/[0.06] group-hover:border-white/[0.12] group-hover:bg-white/[0.04] transition-all duration-500 ease-out overflow-hidden"
		style="transform: rotateX({rotateX}deg) rotateY({rotateY}deg); transition: transform 0.3s cubic-bezier(0.25, 1, 0.5, 1), background 0.5s, border-color 0.5s;"
	>
		<!-- Shine overlay -->
		<div
			class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
			style="
				background: linear-gradient(
					135deg,
					transparent 40%,
					rgba(255, 255, 255, 0.03) 50%,
					transparent 60%
				);
			"
		></div>
		{@render children()}
	</div>
</div>

<style>
	.perspective-1000 {
		perspective: 1000px;
	}
</style>
